import React, { useEffect, useState } from "react";
import moment from "moment";
import { Dropdown } from "semantic-ui-react";
import { CurrentUser } from "../../../../stores/CurrentUserStore/Types";
import { useAppDispatch } from "../../../../stores/CommunityStore";
import { fetchPosts } from "../../../../stores/CommunityStore/posts/actions/fetchPosts";
import { Post } from "../../../../stores/CommunityStore/posts/types";
import Composer from "./Composer";
import PostThread from "./PostThread";
import { postLinkToAttachment } from "./PostThread/linkAttachmentFromMetadata";
import { PostThreadData, ReplyData } from "./PostThread";
import PostsEmptyState from "./PostsEmptyState";

interface PostsProps {
  selectedTopicName: string;
  selectedTopicUuid?: string;
  currentUser: CurrentUser;
  posts: Post[];
  loading: boolean;
  targetReplyId?: string;
}

const replyData = (
  reply: Post["comments"][number]["replies"][number],
  commentId: number,
  postAuthorId: number,
  currentUserId: number,
  currentUserIsAdmin: boolean,
): ReplyData => ({
  id: reply.uuid,
  recordId: reply.id,
  commentId,
  author: reply.user.name,
  profilePictureUrl: reply.user.profile_picture_url,
  time: moment(reply.created_at).fromNow(),
  text: reply.formatted_content,
  likes: reply.likes,
  liked: reply.liked_by_current_user,
  isAuthor: reply.user.id === postAuthorId,
  canDelete: reply.user.id === currentUserId || currentUserIsAdmin,
});

const toPostThreadData = (
  post: Post,
  currentUserId: number,
  currentUserIsAdmin: boolean,
  targetReplyId?: string,
): PostThreadData => ({
  id: post.uuid,
  postId: post.id,
  author: post.user.name,
  profilePictureUrl: post.user.profile_picture_url,
  time: moment(post.created_at).fromNow(),
  isAuthor: post.user.id === currentUserId,
  canDelete: post.user.id === currentUserId || currentUserIsAdmin,
  canEdit: post.user.id === currentUserId || currentUserIsAdmin,
  content: post.content,
  body: post.formatted_content,
  likes: post.likes,
  liked: post.liked_by_current_user,
  replyCount: post.comments.length,
  threadId: `thread-${post.uuid}`,
  threadLabel: `Replies to ${post.user.name}'s post`,
  initiallyExpanded: post.comments.some(
    (comment) =>
      comment.uuid === targetReplyId ||
      comment.replies.some((reply) => reply.uuid === targetReplyId),
  ),
  targetReplyId,
  replies: post.comments.map((comment) => ({
    id: comment.uuid,
    recordId: comment.id,
    commentId: comment.id,
    author: comment.user.name,
    profilePictureUrl: comment.user.profile_picture_url,
    time: moment(comment.created_at).fromNow(),
    text: comment.formatted_content,
    likes: comment.likes,
    liked: comment.liked_by_current_user,
    isAuthor: comment.user.id === post.user.id,
    canDelete: comment.user.id === currentUserId || currentUserIsAdmin,
    replies: comment.replies.map((reply) =>
      replyData(
        reply,
        comment.id,
        post.user.id,
        currentUserId,
        currentUserIsAdmin,
      ),
    ),
  })),
  metadata: post.metadata,
  attachments: (() => {
    const images =
      post.images && post.images.length > 0
        ? post.images.map((src, i) => ({ id: `img-${i}`, src, alt: "Post image" }))
        : [];
    const links = (post.links || []).map(postLinkToAttachment);

    return images.length > 0 || links.length > 0 ? { images, links } : undefined;
  })(),
  isPinned: post.pinned,
  canPin: currentUserIsAdmin,
});

const Posts = ({
  selectedTopicName,
  selectedTopicUuid,
  currentUser,
  posts,
  loading,
  targetReplyId,
}: PostsProps) => {
  const dispatch = useAppDispatch();
  const [sortBy, setSortBy] = useState<string | null>(null);

  useEffect(() => {
    setSortBy(null);
  }, [selectedTopicUuid]);

  const sortPosts = (event, data, mode: string) => {
    event.preventDefault();

    setSortBy(data.text);
    dispatch(
      fetchPosts({
        topicUuid: selectedTopicUuid,
        sortBy: mode,
      }),
    );
  };

  const pinnedPosts = posts.filter((post) => post.pinned);
  const unpinnedPosts = posts.filter((post) => !post.pinned);

  const renderPost = (post: Post) => (
    <PostThread
      post={toPostThreadData(
        post,
        currentUser.id,
        currentUser.admin,
        targetReplyId,
      )}
      currentUserName={currentUser.name}
      currentUserProfilePictureUrl={currentUser.profile_picture}
      key={post.id}
    />
  );

  return (
    <div className="community-shell__posts">
      <div className="community-shell__posts-header">
        <div className="community-shell__posts-icon" aria-hidden="true">
          <svg viewBox="0 0 200 200">
            <use href="#ic-42logo"></use>
          </svg>
        </div>
        <div>
          <h2 className="community-shell__posts-title">
            Welcome back to the community
          </h2>
          <p className="community-shell__posts-copy">
            You’re in good company. Be useful, be curious, and the rest will
            follow.
          </p>
        </div>
      </div>

      <Composer
        currentUser={currentUser}
        selectedTopicUuid={selectedTopicUuid}
      />

      <div className="community-shell__toolbar">
        <span>
          Showing <strong>#{selectedTopicName}</strong>
        </span>
        <div className="community-shell__sort">
          Sort by:{" "}
          <Dropdown
            floating
            inline
            direction="left"
            text={sortBy || ""}
            className="courses-sort-dropdown"
          >
            <Dropdown.Menu>
              <Dropdown.Item
                text="Date"
                onClick={(event, data) => sortPosts(event, data, "date")}
              />
              <Dropdown.Item
                text="Most Replies"
                onClick={(event, data) =>
                  sortPosts(event, data, "most_comments")
                }
              />
              <Dropdown.Item
                text="Likes"
                onClick={(event, data) => sortPosts(event, data, "most_likes")}
              />
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      {loading && <div className="ui active centered inline loader" />}
      {!loading && posts.length === 0 && (
        <PostsEmptyState topicName={selectedTopicName} />
      )}
      {!loading && posts.length > 0 && (
        <>
          {pinnedPosts.length > 0 && (
            <div className="community-shell__pinned-posts">
              <h3 className="community-shell__pinned-title">Pinned</h3>
              {pinnedPosts.map((post) => renderPost(post))}
            </div>
          )}
          {unpinnedPosts.map((post) => renderPost(post))}
        </>
      )}
    </div>
  );
};

export default Posts;
